import { useEffect } from 'react';

/**
 * Applies an HTML image viewer to the specified parent element.
 * @param {string} parentId - The ID of the parent element.
 * @param {(imageSrc: string) => void} onClick - The callback function to handle image click events.
 */
export function applyHtmlImageViewer(
  parentId: string,
  onClick: (imageSrc: string) => void,
) {
  const el = document.querySelector(`[data-id="${parentId}"]`);

  const handleClickImg = (e: MouseEvent) => {
    const src = (e.target as HTMLImageElement)?.src;
    if (src) {
      onClick(src);
    }
  };

  const onMouseOver = (event: Event) => {
    if (event.target instanceof Element && event.target.matches('img')) {
      const img = event.target as HTMLImageElement;
      img.style.cursor = 'pointer';
      img.addEventListener('click', handleClickImg, { once: true });
    }
  };

  const onFocus = (event: Event) => {
    if (event.target instanceof Element && event.target.matches('img')) {
      const img = event.target as HTMLImageElement;
      img.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          handleClickImg(e as unknown as MouseEvent);
        }
      });
    }
  };

  if (el) {
    el.addEventListener('mouseover', onMouseOver);
    el.addEventListener('focus', onFocus, true);
    return () => {
      el.removeEventListener('mouseover', onMouseOver);
      el.removeEventListener('focus', onFocus, true);
    };
  }
}

/**
 * Returns a src of a clicked image in an html element.
 */
export default function useHtmlImageViewer(
  parentId: string,
  onClick: (imageSrc: string) => void,
) {
  useEffect(() => {
    return applyHtmlImageViewer(parentId, onClick);
  });
}
